import api from '../../api'
import { Capacitor } from '@capacitor/core'
import {
  uploadFileInChunks,
  getChunkSize,
  shouldUseDirectUpload,
  GenerateUploadUrlResponse,
  ConfirmUploadRequest,
  ConfirmUploadResponse,
} from '../../../functions/chunkedUpload'
import { uploadBackgroundFile } from '../../../functions/mobileUpload'
import { getFileForUpload } from '../../../functions/filePathHelpers'

export const getPosts = async (data: {
  page: number
  page_size: number
  member_id?: string
  following?: boolean
  liked?: boolean
}) => {
  const res = await api.get('post/', {
    params: data,
  })
  return res.data
}

export const getPostDetail = async (data: { id: string }) => {
  const res = await api.get('post/' + data.id)
  return res.data
}

export const getPostComments = async ({
  id,
  data,
}: {
  id: string
  data: {
    page: number
    page_size: number
  }
}) => {
  const res = await api.get(`post/${id}/comments/`, { params: data })
  return res.data
}

export const postReport = async ({
  id,
  data,
}: {
  id: string
  data: {
    category: string
  }
}) => {
  const res = await api.post(`post/${id}/report/`, data)
  return res.data
}

export const postComment = async ({
  id,
  data,
}: {
  id: string
  data: {
    comment: string
    mentions: string[]
    venue_mentions?: string[]
  }
}) => {
  const res = await api.post(`post/${id}/comments/`, {
    ...data,
    mentions: data.mentions.join(','),
    venue_mentions: (data.venue_mentions ?? []).join(','),
  })
  return res.data
}

export const putLike = async ({
  id,
  data,
}: {
  id: string
  data: {
    like: boolean
  }
}) => {
  const res = await api.put(`post/${id}/likes/`, data)
  return res.data
}

export const postBlock = async ({ id }: { id: string }) => {
  const res = await api.post(`member/${id}/block/`)
  return res.data
}

/**
 * Generate presigned URLs for chunked upload
 * Backend endpoint: POST post/uploads/generate/
 */
export const postGenerateUploadUrl = async (data: {
  filename: string
  filetype: string
  chunk_count: number
  content_type?: string
}): Promise<GenerateUploadUrlResponse> => {
  const res = await api.post('post/uploads/generate/', data, {
    timeout: 60000, // 60 seconds
  })
  return res.data
}

/**
 * Confirm chunked upload completion
 * Backend endpoint: POST post/uploads/confirm/
 */
export const postConfirmUploadUrl = async (
  data: ConfirmUploadRequest
): Promise<ConfirmUploadResponse> => {
  const res = await api.post('post/uploads/confirm/', data, {
    timeout: 60000,
  })
  return res.data
}


export const discardOrphanedUpload = async (uploadedFileId: string): Promise<void> => {
  await api.post('post/uploads/discard/', { uploaded_file_id: uploadedFileId }, { timeout: 15000 })
}

/**
 * Direct upload for small files (< 5MB)
 * Backend endpoint: POST content/upload/
 */
export const postDirectUpload = async (data: {
  file: File
  content_type: string
  onProgress?: (progress: number) => void
}): Promise<string> => {
  const formData = new FormData()
  formData.append('file', data.file)
  formData.append('content_type', data.content_type)

  const res = await api.post('content/upload/', formData, {
    timeout: 60000, // 1 minute for file uploads
    maxBodyLength: Infinity,
    maxContentLength: Infinity,
    onUploadProgress: (progressEvent) => {
      if (progressEvent.total && data.onProgress) {
        const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        data.onProgress(percent)
      }
    },
    // Don't set headers - let axios automatically handle FormData Content-Type with boundary
  })

  return res.data.id || res.data.uploaded_file_id || res.data.file_id
}

/**
 * Upload a file for a post. Returns the uploaded_file_id.
 * Used both for early upload (on cropper Save) and for upload-on-Share.
 */
export const uploadFileForPost = async (params: {
  file: File
  filePath?: string
  onProgress?: (progress: number) => void
  abortSignal?: AbortSignal
  /** Called when presigned URLs are received (chunked flow), so caller can discard on cancel */
  onGenerateResult?: (result: GenerateUploadUrlResponse) => void
}): Promise<string> => {
  const { file, filePath, onProgress, abortSignal, onGenerateResult } = params
  if (!file || !(file instanceof File)) {
    throw new Error('No file provided for upload')
  }
  if (!file.name?.trim() || !file.name.includes('.')) {
    throw new Error('Invalid file: name or extension missing')
  }
  const contentType = file.type.startsWith('video/') ? 'video' : 'photo'

  if (Capacitor.isNativePlatform() && filePath) {
    try {
      const { filePath: nativePath, mimeType } = await getFileForUpload(filePath, file.type)
      const id = await uploadBackgroundFile(
        nativePath,
        mimeType,
        onProgress ?? (() => {}),
        {
          content_type: contentType,
          endpoint: 'content/upload/',
        }
      )
      return id
    } catch (mobileError: any) {
      console.warn('Mobile upload failed, falling back to chunked upload:', mobileError)
      const chunkSize = getChunkSize(file.size)
      const id = await uploadFileInChunks(
        file,
        chunkSize,
        postGenerateUploadUrl,
        postConfirmUploadUrl,
        onProgress,
        { content_type: contentType },
        abortSignal,
        onGenerateResult
      )
      return id
    }
  }
  if (shouldUseDirectUpload(file.size)) {
    const id = await postDirectUpload({
      file,
      content_type: contentType,
      onProgress,
    })
    return id
  }
  const chunkSize = getChunkSize(file.size)
  return uploadFileInChunks(
    file,
    chunkSize,
    postGenerateUploadUrl,
    postConfirmUploadUrl,
    onProgress,
    { content_type: contentType },
    abortSignal,
    onGenerateResult
  )
}

/**
 * Create a post with platform-aware file upload
 * - If uploaded_file_id is provided, skips upload and creates the post with that file
 * - Otherwise uploads the file first (Mobile: capacitor-uploader, Web: chunked/direct)
 */
export const postPost = async (data: {
  file: File | null
  caption: string
  location: string
  mentions: string[]
  venue_mentions?: string[]
  onProgress?: (progress: number) => void
  abortSignal?: AbortSignal
  filePath?: string
  /** When set, upload is skipped and this id is used for the post (early-upload flow) */
  uploaded_file_id?: string
}) => {
  let uploadedFileId: string

  if (data.uploaded_file_id) {
    uploadedFileId = data.uploaded_file_id
  } else {
    if (!data.file) {
      const error: any = new Error('No file provided for upload')
      error.response = {
        data: {
          success: false,
          message: "Post creation failed: {'file': ['File is required']}",
          status_code: 400,
        },
      }
      throw error
    }
    if (!(data.file instanceof File)) {
      const error: any = new Error('Invalid file object')
      error.response = {
        data: {
          success: false,
          message: "Post creation failed: {'file': ['File is required']}",
          status_code: 400,
        },
      }
      throw error
    }
    if (!data.file.name || data.file.name.trim() === '') {
      const error: any = new Error('File name is missing')
      error.response = {
        data: {
          success: false,
          message: "Post creation failed: {'file': ['Invalid file: file name is missing']}",
          status_code: 400,
        },
      }
      throw error
    }
    if (!data.file.name.includes('.')) {
      const error: any = new Error('File must have an extension')
      error.response = {
        data: {
          success: false,
          message: "Post creation failed: {'file': ['Invalid file: file must have an extension']}",
          status_code: 400,
        },
      }
      throw error
    }
    uploadedFileId = await uploadFileForPost({
      file: data.file,
      filePath: data.filePath,
      onProgress: data.onProgress,
      abortSignal: data.abortSignal,
    })
  }

  try {
    const res = await api.post(
      'post/myposts/',
      {
        uploaded_file_id: uploadedFileId, // Primary parameter name
        file_id: uploadedFileId, // Also send for backward compatibility
        caption: data.caption,
        location: data.location,
        mentions: data.mentions.join(','),
        venue_mentions: (data.venue_mentions ?? []).join(','),
      },
      {
        timeout: 30000, // 30 seconds for post creation
      }
    )

    return res.data
  } catch (error: any) {
    // Re-throw with proper error format if it's not already formatted
    if (!error.response) {
      const formattedError: any = new Error(error.message || 'Upload failed')
      formattedError.response = {
        data: {
          success: false,
          message: error.message || 'Post creation failed',
          status_code: 500,
        },
      }
      throw formattedError
    }
    throw error
  }
}

export const putPost = async ({
  id,
  data,
}: {
  id: string
  data: {
    caption: string
    location: string
    mentions: string[]
    venue_mentions?: string[]
  }
}) => {
  const res = await api.patch(`post/${id}/`, {
    ...data,
    mentions: data.mentions.join(','),
    venue_mentions: (data.venue_mentions ?? []).join(','),
  })
  return res.data
}

export const deletePost = async (data: { id: string }) => {
  const res = await api.delete('post/myposts/', { data })
  return res.data
}

export const getSuggestedMembers = async (data: { page: number; page_size: number; exclude_ids?: string }) => {
  const res = await api.get('member/', { params: data })
  return res.data
}

export const getMembers = async (data: {
  search?: string
  page_size?: number
  page?: number
  exclude_ids?: string
  gender?: string
  age_range?: string
  skill_level?: string
  offers_private_lessons?: boolean | null
  fields?: string[]
}) => {
  const res = await api.get('member/', { params: { ...data, fields: data.fields?.join(',') } })
  return res.data
}

export const getVenuesMinimal = async (data: { venue_search: string; page_size?: number }) => {
  const res = await api.get('venues/', {
    params: { minimal: true, venue_search: data.venue_search, page_size: data.page_size ?? 10 },
  })
  return res.data
}
